import { MOBILE_MEDIA_QUERY } from '../../common/components/utils/constants';
import { isRTL } from '../../common/components/utils/utils';

const SELECTORS = {
	mapEle: '#map',
	mapContainer: '#map-container',
	popup: '[data-template-name="map-popup-template"]',
};

const CLASSES = {
	popupRoot: 'clinic-map-popup',
	popupImage: 'clinic-map-popup__image',
	popupBody: 'clinic-map-popup__body',
	popupTitle: 'clinic-map-popup__title',
	popupDescription: 'clinic-map-popup__info',
};

const POPUP_MAX_WIDTH = 390;

const PIN_SIZE = 48;

/**
 * Clinic coordinates object.
 * @typedef {Object} Coords
 * @property {number} lat
 * @property {number} lng
 */

/**
 * Location object for specific clinic.
 * @typedef {Object} Location
 * @property {string} title - Clinic title
 * @property {string} url - Clinic URL
 * @property {string} image
 * @property {string} alt
 * @property {string[]} description
 * @property {Coords} coords - Clinic coordinates that will be used for the pin.
 */

export class CustomMap {
	constructor(options) {
		/**
		 * Element that wraps the map
		 * @type {Element}
		 */
		this.mapContainer = document.querySelector(SELECTORS.mapContainer);
		/**
		 * Element that show the map
		 * @type {Element}
		 */
		this.mapEle = document.querySelector(SELECTORS.mapEle);
		/**
		 * Map object. Is set in onMapInit fn.
		 * @type {Ei}
		 */
		this.map = null;

		const lat = Number(this.mapEle.dataset.centerLat ?? 44.12115705);
		const lng = Number(this.mapEle.dataset.centerLng ?? 20.7912598);

		this.config = CustomMap._combineOptions({
			center: { lat, lng },
			mapContainer: this.mapContainer,
			mapEle: this.mapEle,
			...options,
		});
		this.locations = this.config.locations;
		this.markers = [];

		if ([this.mapEle, this.mapContainer].some((ele) => !ele)) {
			throw new Error('CustomMap not initialized');
		}

		this.mediaQuery = window.matchMedia(MOBILE_MEDIA_QUERY);

		this.bindEvents();
		this.setup();
	}

	static _combineOptions(options) {
		const { LEFT_BOTTOM, RIGHT_BOTTOM } = google.maps.ControlPosition;

		const defaultOptions = {
			mapEle: null,
			mapContainer: null,
			zoom: {
				min: 3,
				max: 17,
			},
			controls: {
				zoomControl: true,
				zoomControlOptions: {
					position: isRTL() ? LEFT_BOTTOM : RIGHT_BOTTOM,
				},
			},
			locations: [],
			onMapClick: () => {},
			// eslint-disable-next-line
			onMarkerClick: (index) => {},
			// eslint-disable-next-line
			onMarkerMouseOver: (index) => {},
			// eslint-disable-next-line
			onMarkerMouseEnd: (index) => {},
		};

		return { ...defaultOptions, ...options };
	}

	/* eslint no-param-reassign: "off" */
	/**
	 * Callback for google maps, called when map is initialized.
	 *
	 * @param {Object} instance
	 */
	static onMapInit(instance) {
		if (!instance.mapEle) {
			return;
		}
		instance.mapStyle = instance.mapStyle || window.MAP_STYLE;

		instance.map = new google.maps.Map(instance.mapEle, {
			center: instance.config.center,
			zoom: 5,
			minZoom: instance.config.zoom.min,
			maxZoom: instance.config.zoom.max,
			fullscreenControl: false,
			mapTypeControl: false,
			streetViewControl: false,
			styles: instance.mapStyle,
			...instance.config.controls,
		});

		instance.iconIdle = {
			url: '/assets/pin.svg', // url
			scaledSize: new google.maps.Size(PIN_SIZE, PIN_SIZE),
			origin: new google.maps.Point(0, 0),
			anchor: new google.maps.Point(PIN_SIZE / 2, PIN_SIZE),
		};

		instance.iconActive = {
			url: '/assets/pin-active.svg', // url
			scaledSize: new google.maps.Size(PIN_SIZE, PIN_SIZE),
			origin: new google.maps.Point(0, 0),
			anchor: new google.maps.Point(PIN_SIZE / 2, PIN_SIZE),
		};

		instance.map.addListener('click', instance.config.onMapClick);
		instance.drawMapMarkers();
	}
	/* eslint no-param-reassign: "error" */

	/**
	 * Bind `this` explicitly for event listeners.
	 */
	bindEvents() {
		['onMapContainerResize', 'onOutsideClick'].forEach((e) => {
			this[e] = this[e].bind(this);
		});
	}

	/**
	 * Is mobile media query met.
	 * @return {boolean}
	 */
	isMobile() {
		return this.mediaQuery.matches;
	}

	// eslint-disable-next-line class-methods-use-this
	getLatLng(obj) {
		return { lat: obj.lat(), lng: obj.lng() };
	}

	// TODO: Add debounce
	/**
	 * When mapContainer is resized, also resize the map(mapEle)
	 */
	onMapContainerResize() {
		if (!this.mapEle || !this.mapContainer) {
			return;
		}
		const toggleOptions = document.querySelector('.clinic-search__view-toggle');
		const height = this.isMobile()
			? this.mapContainer.offsetHeight - toggleOptions.offsetHeight
			: this.mapContainer.offsetHeight;

		/**
		 * Set dimension of map to be fullwidth
		 * @type {number|*}
		 */
		const width = this.mapContainer.offsetWidth;

		this.mapEle.style.width = `${width}px`;
		this.mapEle.style.height = `${height}px`;

		google.maps.event.trigger(this.mapEle, 'resize');
		this.fitBounds();
	}

	setupEventListeners() {
		new ResizeObserver(this.onMapContainerResize).observe(this.mapContainer);
		// On popup window X click
		this.popupWindow.addListener('closeclick', this.onOutsideClick);
	}

	setup() {
		this.isPopupWindowOpen = false;
		this.popupWindow = new google.maps.InfoWindow({
			maxWidth: POPUP_MAX_WIDTH,
		});

		this.setupEventListeners();
	}

	/**
	 * Create an HTML template for the popup window on the map.
	 * Use of `createElement` is to protect from potential XSS.
	 *
	 * @param {Location} location
	 * @return {string}
	 */
	static popupTemplate(location) {
		const { title, url, alt, image, description } = location;

		const popup = document.createElement('div');
		popup.classList.add(CLASSES.popupRoot);

		const img = document.createElement('img');
		img.classList.add(CLASSES.popupImage);
		img.src = image;
		img.alt = alt;

		const body = document.createElement('div');
		body.classList.add(CLASSES.popupBody);

		const heading = document.createElement('a');
		heading.href = url;
		heading.classList.add(CLASSES.popupTitle);
		heading.textContent = title;

		body.appendChild(heading);

		let p;
		description?.forEach((text) => {
			p = document.createElement('p');
			p.classList.add(CLASSES.popupDescription);
			p.textContent = text;
			body.appendChild(p);
		});

		popup.appendChild(img);
		popup.appendChild(body);

		return popup.outerHTML;
	}

	/**
	 * Set locations from outside and draw markers on the map
	 * @param {Location[]} locations
	 */
	setLocations(locations) {
		this.locations = locations;

		this.drawMapMarkers();
	}

	drawMapMarkers() {
		this.markers.forEach((marker) => marker.setMap(null));
		this.markers = [];

		if (!this.locations?.length) {
			return;
		}

		this.bounds = new google.maps.LatLngBounds();

		this.locations.forEach((location, index) => {
			const marker = new google.maps.Marker({
				position: location.coords,
				map: this.map,
				title: location.title,
				icon: this.iconIdle,
			});

			this.markers.push(marker);
			this.bounds.extend(marker.getPosition());

			// define a click behavior
			google.maps.event.addListener(marker, 'click', () => {
				this.popupWindow.close(); // Close previously opened infowindow
				this.popupWindow.setContent(CustomMap.popupTemplate(location));
				this.popupWindow.open(this.map, marker);
				this.isPopupWindowOpen = true;

				this.selectMarker(index);
				CustomMap.trackMarkerClick(location);
			});

			// define a hover behavior
			google.maps.event.addListener(marker, 'mouseover', () => {
				this.config.onMarkerMouseOver(index);
				marker.setIcon(this.iconActive);
			});

			// define a hover ended behavior
			google.maps.event.addListener(marker, 'mouseout', () => {
				this.config.onMarkerMouseEnd(index);
				if (this.activeMarker !== marker) {
					marker.setIcon(this.iconIdle);
				}
			});
		});

		this.map.addListener('click', this.onOutsideClick);

		if (this.markers.length === 1) {
			const [firstMarker] = this.markers;
			firstMarker.setIcon(this.iconActive);
			this.activeMarker = firstMarker;
		}

		this.resetActiveMarker();
		this.fitBounds();
	}

	static trackMarkerClick(location) {
		if (!location) return;

		window.dataLayer.push({
			event: 'view_map_pin',
			item_id: location.id,
			item_name: location.title,
			item_brand: location.brand,
			item_category: location.country,
			item_category2: location.city,
		});
	}

	/**
	 * Select pin on the map.
	 *
	 * @param index
	 */
	selectMarker(index) {
		const marker = this.markers[index];

		if (!marker) {
			return;
		}

		this.resetActiveMarker();

		marker.setIcon(this.iconActive);
		this.activeMarker = marker;
	}

	resetActiveMarker() {
		if (this.activeMarker) {
			this.activeMarker.setIcon(this.iconIdle);
			this.activeMarker = null;
		}
	}

	onOutsideClick() {
		this.resetActiveMarker();
		if (this.isPopupWindowOpen) {
			this.popupWindow?.close();
			this.isPopupWindowOpen = false;
		}
	}

	/**
	 * Pan the map to initial bounds. Useful, when `zoomOnPin` is enabled.
	 */
	fitBounds() {
		if (!this.bounds) return;

		this.map.fitBounds(this.bounds);
	}
}
